"use client";

import React, { useEffect, useState } from "react";
import { Facebook, Instagram, Twitter, MapPin, Phone, Clock, Sandwich, Mail, ArrowUp } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="29519" className="flex flex-col gap-3">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-24 bg-muted/10 animate-pulse rounded" data-index={i} />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-3">
        {[{
        title: "Home",
        path: "/HomePage"
      }, {
        title: "Residential",
        path: "/ResidentialPage"
      }, {
        title: "Commercial",
        path: "/CommercialPage"
      }, {
        title: "Blog",
        path: "/BlogPage"
      }].map((item, i) => <li key={i} data-index={i}>
            <Link to={item.path} className="text-muted-foreground hover:text-[hsl(217,91%,60%)] transition-colors">
              {item.title}
            </Link>
          </li>)}
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-[hsl(217,91%,60%)] transition-colors duration-300 flex items-center gap-2 group">
            <span className="h-px w-0 bg-[hsl(217,91%,60%)] transition-all duration-300 group-hover:w-3" />
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };
  const currentYear = new Date().getFullYear();
  return <footer id="stay-connected" className="dark bg-[hsl(222,47%,4%)] text-foreground pt-24 pb-8 relative border-t border-border/10 overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[hsl(217,91%,60%)]/5 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[hsl(0,84%,60%)]/5 rounded-full blur-[96px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          
          {}
          <div className="space-y-6">
            <Link to="/HomePage" className="block w-fit">
              <img src="https://humorous-shiner.10web.cloud/wp-content/uploads/2026/03/humberg-logo.jpg" alt="Humberg Electrical Services" className="h-12 w-auto object-contain" />
            </Link>
            <p className="text-muted-foreground leading-relaxed max-w-xs">
              Family owned and operated since 2013. Specializing in electrical panel upgrades and 24/7 service calls. No job is too big or too small.
            </p>
            <div className="flex items-center gap-4 pt-2">
              {[{
              icon: Facebook,
              href: "https://facebook.com"
            }, {
              icon: Instagram,
              href: "https://instagram.com"
            }, {
              icon: Twitter,
              href: "https://twitter.com"
            }].map((social, i) => <Link key={i} to={social.href} newTab className="bg-muted/10 p-3 rounded-full text-muted-foreground hover:text-[hsl(217,91%,60%)] hover:bg-[hsl(217,91%,60%)]/10 hover:shadow-[0_0_15px_rgba(0,102,255,0.4)] transition-all duration-300 flex items-center justify-center" data-index={i}>
                  <social.icon className="size-5" />
                </Link>)}
            </div>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-lg font-sans font-bold text-foreground tracking-tight uppercase">Quick Links</h3>
            <MenuProvider menu_id="43" className="w-full">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-lg font-sans font-bold text-foreground tracking-tight uppercase">Get In Touch</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-muted-foreground group">
                <MapPin className="size-5 text-[hsl(217,91%,60%)] shrink-0 mt-1 group-hover:text-[hsl(0,84%,60%)] transition-colors" />
                <span>Richmond, Sugar Land, Missouri City & surrounding Houston areas</span>
              </li>
              <li className="flex items-center gap-3 text-muted-foreground group">
                <Phone className="size-5 text-[hsl(217,91%,60%)] shrink-0 group-hover:text-[hsl(0,84%,60%)] transition-colors" />
                <Link to="tel:+18324053766" className="hover:text-[hsl(217,91%,60%)] transition-colors">
                  832-405-3766
                </Link>
              </li>
              <li className="flex items-center gap-3 text-muted-foreground group">
                <Mail className="size-5 text-[hsl(217,91%,60%)] shrink-0 group-hover:text-[hsl(0,84%,60%)] transition-colors" />
                <Link to="mailto:admin@humbergelectric.com" className="hover:text-[hsl(217,91%,60%)] transition-colors">
                  admin@humbergelectric.com
                </Link>
              </li>
            </ul>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-lg font-sans font-bold text-foreground tracking-tight uppercase">Business Hours</h3>
            <div className="bg-muted/5 border border-border/10 rounded-sm p-6 backdrop-blur-sm hover:bg-muted/10 transition-colors duration-300">
              <div className="flex items-start gap-4">
                <div className="bg-[hsl(217,91%,60%)]/10 p-3 rounded-full text-[hsl(217,91%,60%)]">
                  <Clock className="size-6" />
                </div>
                <div>
                  <p className="font-bold text-foreground mb-1">Standard Hours</p>
                  <p className="text-muted-foreground text-sm mb-2">Mon - Fri: 7AM - 5PM</p>
                  <p className="text-xs text-[hsl(0,84%,60%)] font-bold uppercase tracking-wider">24/7 Emergency Service Calls</p>
                </div>
              </div>
            </div>
          </div>

        </div>

        {}
        <div className="border-t border-border/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
          <p className="text-muted-foreground/60 text-sm">
            &copy; {currentYear} Humberg Electrical Services, LLC. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-muted-foreground/60">
            <Link to="#" className="hover:text-[hsl(217,91%,60%)] transition-colors">Privacy Policy</Link>
            <Link to="#" className="hover:text-[hsl(217,91%,60%)] transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>

      {}
      <Button onClick={scrollToTop} className={`fixed bottom-8 right-8 z-50 size-14 rounded-sm shadow-lg transition-all duration-500 hover:scale-110 hover:shadow-[0_0_20px_rgba(239,68,68,0.6)] bg-[hsl(0,84%,60%)] text-white border-2 border-white/20 p-0 flex items-center justify-center ${showBackToTop ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0"}`} aria-label="Back to top">
        <Sandwich className="size-8" />
      </Button>
    </footer>;
}